import { useRef, useState, type DragEvent, type KeyboardEvent } from "react";
import { Plus, X } from "lucide-react";
import { Button, Card, EmptyState, Icon, IconButton, Modal, Spinner } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { BandAvatar } from "../shared/BandAvatar.js";
import { BandProfileBrowser } from "./BandProfileModal.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { useTop8, useSaveTop8, type Top8Slot } from "../../app/hooks/useTop8.js";
import { useFriends, bandInitials, type FriendSummary } from "../../app/hooks/useFriends.js";
import {
  TOP8_DRAG_TYPE,
  TOP8_FRIEND_DRAG_TYPE,
  decodeDrag,
  encodeDrag,
  gridFromSlots,
  gridToWrite,
  pinToSlot,
  sameGrid,
  swapSlots,
  vacateSlot,
  type Top8Grid,
} from "./top8Drag.js";
import styles from "./Top8Card.module.scss";

/**
 * The Top 8 grid (the "MySpace for working bands" social-only grid) on
 * `/contacts`. Gated on `contacts:write` (the top8 routes' gate — distinct from the friends:manage base
 * routes): a read-only seat never mounts it. The grid is edited locally (add from accepted friends, remove,
 * reorder) and PUT as the FULL grid on Save. Only ACCEPTED friends are addable and positions are 1..8 (both
 * server-validated). Designed empty / loading / error states.
 */
export function Top8Card({ onOpenBand }: { onOpenBand?: (bandId: string) => void } = {}) {
  const canWrite = useCapability("contacts:write");
  // GET/PUT /top8 require contacts:write — a read-only seat can't load it, so the card never mounts.
  if (!canWrite) return null;
  return <Top8CardInner onOpenBand={onOpenBand} />;
}

/** A FriendSummary (accepted friend) projected into a Top 8 slot tile. */
function friendToSlot(f: FriendSummary, position: number): Top8Slot {
  return {
    position,
    friendBandId: f.friendBandId,
    bandName: f.bandName,
    slug: f.slug,
    avatarMediaId: f.avatarMediaId,
  };
}

function Top8CardInner({ onOpenBand }: { onOpenBand?: (bandId: string) => void }) {
  const query = useTop8();
  const save = useSaveTop8();

  // The grid is POSITIONAL: eight slots, holes allowed. Pinning to slot 6 leaves 1–5 empty rather than
  // compacting, because the arrangement is the point of a Top 8.
  const server = gridFromSlots(query.data?.slots);
  // An in-flight write's optimistic grid. null = show server truth.
  const [optimistic, setOptimistic] = useState<Top8Grid | null>(null);
  const [pickingSlot, setPickingSlot] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  // The band whose profile is open (desktop browse modal). A filled tile opens it exactly as a Friends row
  // does: the route's callback when one is supplied (mobile's dedicated screen), else the modal in place.
  const [browsing, setBrowsing] = useState<string | null>(null);
  // True from a tile's dragstart until just after its dragend, so the click some engines dispatch at the end
  // of a drag never opens a profile — a reorder must stay a reorder.
  const dragging = useRef(false);

  function openBand(bandId: string) {
    if (dragging.current) return;
    if (onOpenBand) onOpenBand(bandId);
    else setBrowsing(bandId);
  }

  const slots = optimistic ?? server;
  const chosen = new Set(slots.map((s) => s?.friendBandId).filter(Boolean));

  /**
   * Every mutation writes immediately (there is no Save button): update optimistically, PUT the full grid,
   * and on failure drop back to server truth — useSaveTop8 surfaces the error as a toast, so the UI never
   * silently diverges from what was actually stored.
   */
  function commit(next: Top8Grid) {
    if (sameGrid(next, slots)) return;
    setOptimistic(next);
    save.mutate(gridToWrite(next), {
      onSuccess: () => setOptimistic(null),
      onError: () => setOptimistic(null),
    });
  }

  function onDropOnSlot(index: number, e: DragEvent<HTMLElement>) {
    e.preventDefault();
    setDragOver(null);
    const payload = decodeDrag(e.dataTransfer.getData(TOP8_DRAG_TYPE) || e.dataTransfer.getData("text/plain"));
    if (!payload) return;
    if (payload.kind === "slot") commit(swapSlots(slots, payload.index, index));
    else commit(pinToSlot(slots, index, payload.band));
  }

  function allowDrop(index: number, e: DragEvent<HTMLElement>) {
    if (!e.dataTransfer.types.includes(TOP8_DRAG_TYPE)) return;
    e.preventDefault();
    // The dropEffect must sit within the SOURCE's effectAllowed or Chromium cancels the drop: a friend
    // drag is a copy (the row stays in Friends), a slot drag is a move. Payload values are unreadable
    // during dragover — the friend MARKER TYPE is how we tell them apart (see top8Drag.ts).
    e.dataTransfer.dropEffect = e.dataTransfer.types.includes(TOP8_FRIEND_DRAG_TYPE) ? "copy" : "move";
    setDragOver(index);
  }

  // No header action: the grid IS the affordance — every empty slot is its own add button.
  return (
    <Card id="top8" title="Top 8" flush>
      <div className={styles.body}>
        {query.isLoading ? (
          <div className={styles.center} role="status" aria-live="polite">
            <Spinner size={20} label="Loading Top 8…" />
          </div>
        ) : query.isError ? (
          <Top8Error error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <>
            {/* Born-blank renders the eight empty slots and nothing else — the dashed grid is the empty
                state, so there is no separate hint line above it. */}
            <ol className={styles.grid}>
              {slots.map((slot, i) => {
                // EVERY tile is a drop target, filled or empty; only filled tiles are drag sources.
                const dropProps = {
                  onDragOver: (e: DragEvent<HTMLElement>) => allowDrop(i, e),
                  onDragLeave: () => setDragOver((d) => (d === i ? null : d)),
                  onDrop: (e: DragEvent<HTMLElement>) => onDropOnSlot(i, e),
                  "data-drag-over": dragOver === i || undefined,
                };

                if (!slot) {
                  return (
                    <li key={`empty-${i}`} className={styles.tileEmpty} {...dropProps}>
                      <button
                        type="button"
                        className={styles.addSlot}
                        aria-label={`Add a band to Top 8 slot ${i + 1}`}
                        onClick={() => setPickingSlot(i)}
                      >
                        <span className={styles.addGlyph} aria-hidden="true">
                          <Icon icon={Plus} size={24} />
                        </span>
                      </button>
                    </li>
                  );
                }

                return (
                  <li
                    key={slot.friendBandId ?? i}
                    className={styles.tile}
                    draggable
                    onDragStart={(e) => {
                      dragging.current = true;
                      e.dataTransfer.setData(TOP8_DRAG_TYPE, encodeDrag({ kind: "slot", index: i }));
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragEnd={() => {
                      // Cleared on the next tick: the trailing click (where an engine sends one) lands first.
                      setTimeout(() => {
                        dragging.current = false;
                      }, 0);
                    }}
                    {...dropProps}
                  >
                    <Top8Tile
                      slot={slot}
                      onOpen={() => slot.friendBandId && openBand(slot.friendBandId)}
                      onRemove={() => commit(vacateSlot(slots, i))}
                    />
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </div>

      {browsing && <BandProfileBrowser initialBandId={browsing} onClose={() => setBrowsing(null)} />}

      {pickingSlot !== null && (
        <Top8Picker
          chosen={chosen}
          onAdd={(f) => {
            const target = pickingSlot >= 0 ? pickingSlot : slots.findIndex((s) => !s);
            if (target >= 0) commit(pinToSlot(slots, target, friendToSlot(f, target + 1)));
            setPickingSlot(null);
          }}
          onClose={() => setPickingSlot(null)}
        />
      )}
    </Card>
  );
}

/**
 * A FILLED Top 8 tile (ART-172): the friend band's photo fills the whole square, with the name overlaid on a
 * subtle dark bottom gradient so white text stays legible; no reorder arrows (dragging is the rearrangement).
 * Owns its own `useAvatarUrl` — one hook per tile is the required shape for a grid of N band photos, since
 * `BandAvatar`/`Avatar` render a fixed-size identity mark rather than filling an arbitrary box. Falls back to
 * centred initials when the band has no picture.
 */
function Top8Tile({ slot, onOpen, onRemove }: { slot: Top8Slot; onOpen: () => void; onRemove: () => void }) {
  const url = useAvatarUrl("band", slot.friendBandId, slot.avatarMediaId);
  const name = slot.bandName || "band";
  // The open control is a `div role="button"`, not a `<button>`: the tile (its `<li>`) is the drag source,
  // and Firefox will not start a drag from inside a native button. Enter/Space open it like a button.
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen();
    }
  }
  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className={styles.open}
        aria-label={`Open ${name}'s profile`}
        onClick={onOpen}
        onKeyDown={onKeyDown}
      >
        {url ? (
          <img className={styles.photo} src={url} alt="" draggable={false} />
        ) : (
          <span className={styles.initials} aria-hidden="true">
            {bandInitials(slot.bandName)}
          </span>
        )}
      </div>
      <IconButton
        icon={X}
        size="sm"
        tone="invert"
        label={`Remove ${slot.bandName || "band"} from Top 8`}
        onClick={onRemove}
        className={styles.remove}
      />
      <span className={styles.tileName}>{slot.bandName || "Unnamed band"}</span>
    </>
  );
}

// ---- add-from-accepted-friends picker -------------------------------------------------------------------

/**
 * The add picker — lists the band's ACCEPTED (active) friends not already in the grid; picking one pins it to
 * the slot that opened the picker (the keyboard/pointer path to what dragging a friend row does).
 * Only active friends are selectable. Hand-rolled modal chrome (the ProfileModal precedent). Uses
 * useFriends (friends:manage); its own loading / empty / error states cover the fetch.
 */
function Top8Picker({
  chosen,
  onAdd,
  onClose,
}: {
  chosen: Set<string | undefined>;
  onAdd: (f: FriendSummary) => void;
  onClose: () => void;
}) {
  const friends = useFriends();

  const selectable = (friends.data?.active ?? []).filter((f) => !chosen.has(f.friendBandId));

  return (
    <Modal title="Add to Top 8" onClose={onClose}>
      {friends.isLoading ? (
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner size={20} label="Loading friends…" />
        </div>
      ) : friends.isError ? (
        <EmptyState
          title="Couldn't load friends"
          description={friends.error instanceof Error ? friends.error.message : "Please try again."}
          action={<Button onClick={() => void friends.refetch()}>Retry</Button>}
        />
      ) : (friends.data?.active ?? []).length === 0 ? (
        <EmptyState title="No friends yet" description="Add a friend first — only accepted friends can join your Top 8." />
      ) : selectable.length === 0 ? (
        <EmptyState title="All added" description="Every friend is already in your Top 8." />
      ) : (
        <ul className={styles.pickList}>
          {selectable.map((f) => (
            <li key={f.friendBandId}>
              <button type="button" className={styles.pickRow} onClick={() => onAdd(f)}>
                <BandAvatar bandId={f.friendBandId} avatarMediaId={f.avatarMediaId} initials={bandInitials(f.bandName)} />
                <span className={styles.pickMain}>
                  <span className={styles.pickName}>{f.bandName || "Unnamed band"}</span>
                  {f.slug && <span className={styles.pickSlug}>@{f.slug}</span>}
                </span>
                <Icon icon={Plus} size={20} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

// ---- error state ----------------------------------------------------------------------------------------

function Top8Error({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;
  if (status === 403) {
    return <EmptyState title="Top 8 unavailable" description="Your role on this band doesn't include the Top 8." />;
  }
  const message = error instanceof Error && error.message ? error.message : "Please try again.";
  return (
    <EmptyState title="Couldn't load your Top 8" description={message} action={<Button onClick={onRetry}>Retry</Button>} />
  );
}
